import { useState, useMemo } from 'react';
import SectionTitle from '../../../components/atoms/SectionTitle';
import SectionDesc from '../../../components/atoms/SectionDesc';
import CustomModal from '../../../components/atoms/CustomModal';
import Input from '../../../components/atoms/Input';
import Button from '../../../components/atoms/Button';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import SlotsSection from './SlotsSection';
import {
  PromotionSlot,
  PromotionSlotItem,
  MarketplaceScopeCode,
  PromotionTypeCode,
} from '../../../types/promotionSlots';
import {
  useGetPromotionSlotsQuery,
  useUpdatePromotionSlotMutation,
} from '../../../store/services/promotionSlotsService';

const PROMOTION_TYPE_ORDER: Record<string, number> = {
  HERO_BANNER: 1,
  TOP_PRODUCTS: 2,
  FLASH_DEALS: 3,
};

export default function PromotionsSlotsPage() {
  const { t } = useTranslation();

  const { data: response, isLoading } = useGetPromotionSlotsQuery();
  const [updatePromotionSlot, { isLoading: isUpdating }] =
    useUpdatePromotionSlotMutation();

  const [selectedItem, setSelectedItem] = useState<PromotionSlot | null>(null);

  const schema = z.object({
    numberOfSlots: z.preprocess(
      (val) =>
        val === '' || val === undefined || val === null
          ? undefined
          : Number(val),
      z
        .number({
          required_error: t('components.validations.requiredField') as string,
          invalid_type_error: t('components.validations.requiredField') as string,
        })
        .min(0, t('components.validations.requiredField') as string)
    ),
  });

  type FormData = z.infer<typeof schema>;

  const { control, handleSubmit, reset } = useForm<FormData>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {
      numberOfSlots: undefined,
    },
  });

  const getSlotTypeDetails = (
    promotionTypeCode: PromotionTypeCode,
    marketplaceScopeCode: MarketplaceScopeCode
  ) => {
    const isMain = marketplaceScopeCode === 'MAIN_MARKETPLACE';
    const marketSuffix = isMain
      ? t('home.slots-main-market-suffix', '(main market)')
      : t('home.slots-category-market-suffix', '(category market)');

    switch (promotionTypeCode) {
      case 'HERO_BANNER':
        return {
          type: `${t('home.hero-banners', 'Hero banners')} ${marketSuffix}`,
          unit: t('home.unit-banners', 'banners'),
        };
      case 'TOP_PRODUCTS':
        return {
          type: `${t('home.top-products', 'Top products')} ${marketSuffix}`,
          unit: t('home.unit-products', 'products'),
        };
      case 'FLASH_DEALS':
        return {
          type: `${t('home.flash-deals', 'Flash deals')} ${marketSuffix}`,
          unit: t('home.unit-deals', 'deals'),
        };
      default:
        return {
          type: `${promotionTypeCode} ${marketSuffix}`,
          unit: '',
        };
    }
  };

  const mapToUiSlot = (item: PromotionSlotItem): PromotionSlot => {
    const { type, unit } = getSlotTypeDetails(
      item.promotionTypeCode,
      item.marketplaceScopeCode
    );
    return {
      id: item.id,
      marketplaceScopeCode: item.marketplaceScopeCode,
      promotionTypeCode: item.promotionTypeCode,
      type,
      numberOfSlots: item.slotNo,
      unit,
    };
  };

  const sortByOrder = (a: PromotionSlot, b: PromotionSlot) => {
    const orderA = PROMOTION_TYPE_ORDER[a.promotionTypeCode || ''] ?? 99;
    const orderB = PROMOTION_TYPE_ORDER[b.promotionTypeCode || ''] ?? 99;
    return orderA - orderB;
  };

  const slotsList: PromotionSlotItem[] = useMemo(() => {
    return response?.payload || [];
  }, [response]);

  const mainMarketSlots = useMemo(() => {
    return slotsList
      .filter((item) => item.marketplaceScopeCode === 'MAIN_MARKETPLACE')
      .map(mapToUiSlot)
      .sort(sortByOrder);
  }, [slotsList, t]);

  const categoryMarketSlots = useMemo(() => {
    return slotsList
      .filter((item) => item.marketplaceScopeCode === 'CATEGORY_MARKETPLACE')
      .map(mapToUiSlot)
      .sort(sortByOrder);
  }, [slotsList, t]);

  const handleEdit = (item: PromotionSlot) => {
    setSelectedItem(item);
    reset({
      numberOfSlots: item.numberOfSlots,
    });
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
    reset();
  };

  const onSubmit = async (data: FormData) => {
    if (!selectedItem) return;

    try {
      await updatePromotionSlot({
        id: selectedItem.id,
        slotNo: data.numberOfSlots,
      }).unwrap();
      message.success(
        t('components.updatedSuccessfully', 'Updated successfully')
      );
      handleCloseModal();
    } catch (error: any) {
      const errData = error?.data;
      const errorDetail = errData?.errors
        ? (Object.values(errData.errors)[0] as string)
        : null;
      message.error(
        errorDetail ||
          errData?.message ||
          t('components.errorOccurred', 'Error updating slot')
      );
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col gap-1">
        <SectionTitle text={t('home.slots-title')} />
        <SectionDesc text={t('home.slots-page-desc')} />
      </div>

      {/* Main Market Place Section */}
      <SlotsSection
        title={t('home.slots-main-market-title')}
        data={mainMarketSlots}
        isLoading={isLoading}
        onEdit={handleEdit}
      />

      {/* Category Market Place Section */}
      <SlotsSection
        title={t('home.slots-category-market-title')}
        data={categoryMarketSlots}
        isLoading={isLoading}
        onEdit={handleEdit}
      />

      <CustomModal
        isOpen={!!selectedItem}
        onClose={handleCloseModal}
        title={t('home.slots-edit-title')}
        subTitle={t('home.slots-edit-desc')}
        width={600}
        closeIconClassName="[&_.ant-modal-close]:!top-8"
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-6 mt-4"
        >
          <Input
            id="slot-type"
            label={t('home.slots-type')}
            value={selectedItem?.type || ''}
            disabled
            className="!rounded-xl"
          />
          <Controller
            name="numberOfSlots"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                id="slot-number"
                label={t('home.slots-number-of-slots')}
                placeholder={t('home.set-number')}
                type="number"
                {...field}
                value={field.value ?? ''}
                status={error ? 'error' : undefined}
                helperText={error?.message}
                className="!rounded-xl"
              />
            )}
          />
          <div className="flex justify-end gap-3 mt-2">
            <Button
              variant="outline"
              type="button"
              onClick={handleCloseModal}
              className="rounded-xl w-40"
              disabled={isUpdating}
            >
              {t('home.slots-cancel')}
            </Button>
            <Button
              type="submit"
              className="rounded-xl w-36"
              loading={isUpdating}
            >
              {t('home.slots-edit-btn')}
            </Button>
          </div>
        </form>
      </CustomModal>
    </div>
  );
}
